import React from "react";
import { createRoot } from "react-dom/client";

import "@hotwired/turbo-rails";
import { Application } from "@hotwired/stimulus";
import ClaimsExperienceSelectorController from "./controllers/claims_experience_selector_controller";
import ClaimsExperienceBreakdownSelectorController from "./controllers/claims_experience_breakdown_selector_controller";
import RenewalSelectionModalController from "./controllers/renewal_selection_modal_controller";
import BreakdownSelectionModalController from "./controllers/breakdown_selection_modal_controller";
import BreakdownModalTriggerController from "./controllers/breakdown_modal_trigger_controller";
import ClaimsExperienceSelectionModalController from "./controllers/claims_experience_selection_modal_controller";
import ClaimsExperienceModalTriggerController from "./controllers/claims_experience_modal_trigger_controller";
import PlanDesignSelectionModalController from "./controllers/plan_design_selection_modal_controller";
import PlanDesignSelectionModalTriggerController from "./controllers/plan_design_selection_modal_trigger_controller";
import PlanDesignModalTriggerController from "./controllers/plan_design_modal_trigger_controller";
import ColorSyncController from "./controllers/color_sync_controller";
import DropdownController from "./controllers/dropdown_controller";
import DemographicsChartController from "./controllers/demographics_chart_controller";
import SimpleChartController from "./controllers/simple_chart_controller";
import ClaimsBreakdownChartController from "./controllers/claims_breakdown_chart_controller";
import ServiceReportChartController from "./controllers/service_report_chart_controller";
import EditableRateSheetController from "./controllers/editable_rate_sheet_controller";
import EditableCellController from "./controllers/editable_cell_controller";
import RateSheetEditorController from "./controllers/rate_sheet_editor_controller";
import SimpleFieldEditorController from "./controllers/simple_field_editor_controller";
import ClaimsExperienceEditorController from "./controllers/claims_experience_editor_controller";
import ClaimsExperienceBreakdownEditorController from "./controllers/claims_experience_breakdown_editor_controller";
import TextWithRecommendationsController from "./controllers/text_with_recommendations_controller";
import PlanDesignFieldController from "./controllers/plan_design_field_controller";
import ModalFieldEditController from "./controllers/modal_field_edit_controller";
import RenewalClaimsExperienceSelectorController from "./controllers/renewal_claims_experience_selector_controller";
import RenewalClaimsBreakdownSelectorController from "./controllers/renewal_claims_breakdown_selector_controller";
import RenewalDemographicBreakdownEditorController from "./controllers/renewal_demographic_breakdown_editor_controller";
import AlternateFieldEditorController from "./controllers/alternate_field_editor_controller";
import AutoSaveController from "./controllers/auto_save_controller";
import RtqPastRatesController from "./controllers/rtq_past_rates_controller";
import RtqDisabilityClaimsController from "./controllers/rtq_disability_claims_controller";
import RtqClaimsExperiencesController from "./controllers/rtq_claims_experiences_controller";
import FileDropZoneController from "./controllers/file_drop_zone_controller";
import ExpectedOutputEditorController from "./controllers/expected_output_editor_controller";
import VisibilityVariablesController from "./controllers/visibility_variables_controller";
import RequiredFieldController from "./controllers/required_field_controller";
import WorkflowRefreshController from "./controllers/workflow_refresh_controller";
import AutoSubmitController from "./controllers/auto_submit_controller";
import ExtractButton from "./extract_button";
import ShowNewFile from "./show_new_file";
import SourceAttributionModalButton from "./source_attribution_modal_button";
import SourceAttributionIconButton from "./source_attribution_icon_button";
import FieldValueEdit from "./field_value_edit";
import FlashMessages from "./flash_messages";
import ExportDropdown from "./export_dropdown";
import FileUpload from "./file_upload";
import NewDocumentForm from "./new_document_form";
import IngestionForm from "./ingestion_form";
import BatchUploadRenewalDocumentsForm from "./batch_upload_renewal_documents_form";
import BatchUploadBookletsForm from "./batch_upload_booklets_form";

const components = {
  ExtractButton: ExtractButton,
  ShowNewFile: ShowNewFile,
  SourceAttributionModalButton: SourceAttributionModalButton,
  SourceAttributionIconButton: SourceAttributionIconButton,
  FieldValueEdit: FieldValueEdit,
  FlashMessages: FlashMessages,
  ExportDropdown: ExportDropdown,
  FileUpload: FileUpload,
  NewDocumentForm: NewDocumentForm,
  IngestionForm: IngestionForm,
  BatchUploadRenewalDocumentsForm: BatchUploadRenewalDocumentsForm,
  BatchUploadBookletsForm: BatchUploadBookletsForm,
};

const controllers = {
  "claims-experience-selector": ClaimsExperienceSelectorController,
  "claims-experience-breakdown-selector": ClaimsExperienceBreakdownSelectorController,
  "renewal-selection-modal": RenewalSelectionModalController,
  "breakdown-selection-modal": BreakdownSelectionModalController,
  "breakdown-modal-trigger": BreakdownModalTriggerController,
  "claims-experience-selection-modal": ClaimsExperienceSelectionModalController,
  "claims-experience-modal-trigger": ClaimsExperienceModalTriggerController,
  "plan-design-selection-modal": PlanDesignSelectionModalController,
  "plan-design-selection-modal-trigger": PlanDesignSelectionModalTriggerController,
  "plan-design-modal-trigger": PlanDesignModalTriggerController,
  "color-sync": ColorSyncController,
  "dropdown": DropdownController,
  "demographics-chart": DemographicsChartController,
  "simple-chart": SimpleChartController,
  "claims-breakdown-chart": ClaimsBreakdownChartController,
  "service-report-chart": ServiceReportChartController,
  "editable-rate-sheet": EditableRateSheetController,
  "editable-cell": EditableCellController,
  "rate-sheet-editor": RateSheetEditorController,
  "simple-field-editor": SimpleFieldEditorController,
  "claims-experience-editor": ClaimsExperienceEditorController,
  "claims-experience-breakdown-editor": ClaimsExperienceBreakdownEditorController,
  "text-with-recommendations": TextWithRecommendationsController,
  "plan-design-field": PlanDesignFieldController,
  "modal-field-edit": ModalFieldEditController,
  "renewal-claims-experience-selector": RenewalClaimsExperienceSelectorController,
  "renewal-claims-breakdown-selector": RenewalClaimsBreakdownSelectorController,
  "renewal-demographic-breakdown-editor": RenewalDemographicBreakdownEditorController,
  "alternate-field-editor": AlternateFieldEditorController,
  "auto-save": AutoSaveController,
  "rtq-past-rates": RtqPastRatesController,
  "rtq-disability-claims": RtqDisabilityClaimsController,
  "rtq-claims-experiences": RtqClaimsExperiencesController,
  "file-drop-zone": FileDropZoneController,
  "expected-output-editor": ExpectedOutputEditorController,
  "visibility-variables": VisibilityVariablesController,
  "required-field": RequiredFieldController,
  "workflow-refresh": WorkflowRefreshController,
  "auto-submit": AutoSubmitController,
};

window.Stimulus = Application.start();

for (const [name, Controller] of Object.entries(controllers)) {
  Stimulus.register(name, Controller)
};

document.addEventListener("turbo:load", () => {
  const element = document.getElementById("show-new-file");
  if (element) {
    const root = createRoot(element);
    root.render(<ShowNewFile />);
  }
})

const mountReactComponents = (domElement) => {
  domElement = domElement || document;
  for (const componentName in components) {
    const elements = domElement.querySelectorAll(`[data-component="${componentName}"]`);
    elements.forEach(element => {
      const root = createRoot(element);
      try {
        const props = element.dataset.props ? JSON.parse(element.dataset.props) : {};
        const Component = components[componentName];
        root.render(<Component {...props} />);
      } catch (e) {
        console.error(`Error mounting component ${componentName}:`, e);
      }
    });
  }
};

window.mountReactComponents = mountReactComponents;

document.addEventListener("turbo:load", () => {mountReactComponents()});
